import LiveMetricController from "../../report/live_metric_controller";

// The "Build a live chart" tab of the tenant board's Add chart dialog. Stages an unsaved card through the
// add-card controller on the surrounding edit grid, or navigates to the edit page with the metric
// staged from :show.
export default class extends LiveMetricController {
  // Stimulus merges a subclass's statics with its parent's, so this declares only what the base does not:
  // the show page opens the dialog with a URL template instead of staging in place, and the footer
  // relabels itself when the pencil opens this tab on an existing card.
  static values = { showUrlTemplate: String };
  static targets = ["pinNewButton", "addLabel", "addIcon", "heading", "blurb"];

  connect() {
    // A name the user never touched keeps tracking the selection; once they type, it is theirs.
    this.nameIsGenerated = true;
    this.syncAll();
    // Switching to this tab resets the form and focuses the first control, the way opening the old
    // standalone dialog did. Watches the tab panel and the dialog rather than this element: the classes
    // that hide it are toggled on those, not here.
    this.observeOpen();
  }

  disconnect() {
    super.disconnect();
    this.openObserver?.disconnect();
  }

  observeOpen() {
    this.openObserver = new MutationObserver(() => {
      const open = !this.hidden;
      if (open && !this.wasOpen) this.reset();
      this.wasOpen = open;
    });
    this.wasOpen = !this.hidden;
    [this.panel, this.dialog].forEach((el) => {
      if (el) this.openObserver.observe(el, { attributes: true, attributeFilter: ["class", "data-state"] });
    });
  }

  // Reopening the dialog on an already-active Live metric tab toggles no class on this panel, so the
  // observer sees nothing — the dialog's own open event covers that case.
  dialogOpened() {
    if (!this.hidden) this.reset();
  }

  // Cancelling an edit has to drop the card, or the NEXT Add would rewrite it instead of staging a card.
  dialogClosed() {
    this.editingCard = null;
    this.syncFooter();
  }

  reset() {
    this.staged = false;
    // An edit seeds the form from its card and then opens the dialog, so a reset here would throw the
    // seeded selection away before the author ever saw it. Only the name is left alone; staged is not,
    // or a second edit in one visit would refuse to apply.
    if (this.editingCard) return;

    // Reused across opens, so reset everything a previous Add left behind.
    this.nameIsGenerated = true;
    this.syncName();
    this.focusFirstControl();
  }

  get panel() {
    return this.element.closest("[data-ui--tabs-target='panel']");
  }

  get dialog() {
    return this.element.closest("[data-ui--dialog-target='dialog']");
  }

  // Hidden either because the dialog is closed or because the sibling tab is showing. The panel's own
  // hiding is data-state driven (data-[state=inactive]:hidden) as well as class-toggled, so both are read.
  get hidden() {
    const panel = this.panel;
    if (panel && (panel.classList.contains("hidden") || panel.dataset.state === "inactive")) return true;

    return !!this.dialog?.classList.contains("hidden");
  }

  // The metric select is a button-triggered combobox, so focus goes to its trigger rather than an input.
  focusFirstControl() {
    this.element.querySelector("[data-select-target='trigger']")?.focus();
  }

  // Enter anywhere in the dialog commits, except from the metric combobox — that control uses Enter to
  // pick the highlighted option, and stealing it would make the list unusable by keyboard.
  submitOnEnter(event) {
    if (event.target.closest("[data-controller~='select']")) return;
    // Bound at the panel container, so this also sees Enter on Cancel. Preventing its native activation
    // would stage a card where the user asked to close.
    if (event.target.closest("button")) return;
    // A textarea takes multi-line prose, so Enter is a newline there rather than a submit.
    if (event.target.matches("textarea")) return;

    event.preventDefault();
    // Blurred first so the field's own focusout sanitizer runs: Enter would otherwise stage — and, on
    // the show page, put in a URL — whatever was typed, card number included.
    event.target.blur();
    // add() closes the dialog itself once staging resolves. Closing here too would race the POST exactly
    // as the button's parallel close action did: the dialog vanishes, and a quick Save submits before the
    // hidden metric fields land, dropping the card while reporting success.
    this.add();
  }

  loadPreview() {
    if (!this.hasPreviewTarget) return;

    const params = new URLSearchParams({
      metric_key: this.metricTarget.value,
      dimension: this.selectedDimension(),
      ...this.presentOnly({
        series: this.selectedSeries(),
        companion: this.selectedCompanion(),
        limit: this.selectedLimit(),
        sort: this.hasSortTarget ? this.sortTarget.value : "",
        card_type: this.hasChartTypeTarget ? this.chartTypeTarget.value : "",
        rollup: this.hasRollupTarget && this.rollupTarget.checked && this.selectedLimit() !== "" ? "1" : "",
        target_lines: this.selectedTargetLines(),
      }),
    });
    // setAttribute rather than .src: the attribute is what Turbo observes to refetch, and it is set even
    // where the custom element's property accessor isn't defined.
    this.previewTarget.setAttribute("src", `${this.previewUrlValue}?${params}`);
  }

  // The fields this surface does not offer report "" rather than being absent, so they are dropped here
  // instead of travelling as empty values.
  presentOnly(fields) {
    return Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== ""));
  }

  // Staging goes through the add-card controller on the surrounding scope, which owns the POST and the
  // turbo_stream append — the same path the section and chart items use. :show has no live grid, so it
  // navigates to the edit page with the metric staged instead.
  async add() {
    // Again-safe: the dialog closes on Add, but Enter and a double-click can both land before it does,
    // and each would stage its own card.
    if (this.staged) return;

    this.staged = true;
    const metricKey = this.metricTarget.value;
    const dimension = this.selectedDimension();
    // Blank so the server names it: the client's precomputed names are keyed on dimension|series only,
    // so a ranked chart would persist "Items Sold by Item" where the server writes "Top 8 items by ...".
    const name = this.nameIsGenerated ? "" : this.nameTarget.value.trim();

    // Blanks are dropped rather than sent empty: a control this surface never offered must not travel as
    // a choice the author made and left blank.
    const extra = this.presentOnly({
      subtitle: this.element.querySelector("[id$='live_metric_subtitle']")?.value.trim() || "",
      description:
        this.element.querySelector("[id$='live_metric_description']")?.value.trim() || "",
      series: this.selectedSeries(),
      companion: this.selectedCompanion(),
      limit: this.selectedLimit(),
      sort: this.hasSortTarget ? this.sortTarget.value : "",
      card_type: this.hasChartTypeTarget ? this.chartTypeTarget.value : "",
      rollup: this.hasRollupTarget && this.rollupTarget.checked && this.selectedLimit() !== "" ? "1" : "",
      target_lines: this.selectedTargetLines(),
    });

    if (this.editingCard) {
      // Rewrites the card's own hidden fields rather than staging a second card, so the edit keeps the
      // card's id, position and place in the grid. Nothing is persisted until the dashboard's Save --
      // the same as an add, a rename or a resize.
      this.applyToCard(this.editingCard, { metric_key: metricKey, dimension, name, ...extra });
      this.editingCard = null;
      this.syncFooter();
      this.closeDialog();
    } else if (this.hasShowUrlTemplateValue && this.showUrlTemplateValue) {
      const filled = this.showUrlTemplateValue
        .replace("METRIC_KEY", encodeURIComponent(metricKey))
        .replace("DIMENSION", encodeURIComponent(dimension))
        .replace("NAME", encodeURIComponent(name));
      // Assigning the template untouched when there is nothing to add keeps the common path free of URL
      // parsing, and of the origin it would have to resolve against.
      const params = Object.entries(extra);
      if (params.length === 0) {
        window.location.assign(filled);
      } else {
        // Prefixed to match the show page's pending_* readers.
        const url = new URL(filled, window.location.origin);
        params.forEach(([key, value]) => url.searchParams.set(`pending_${key}`, value));
        window.location.assign(`${url.pathname}${url.search}`);
      }
    } else {
      // Awaited, and the dialog closes only afterwards: otherwise a quick Save submits before the hidden
      // metric fields land, reporting success while dropping the card.
      await this.addCardController().stage({ metric_key: metricKey, dimension, name, ...extra });
      this.closeDialog();
    }
  }

  // Every field build_realtime reads, so an edit that clears one clears it on the card too — leaving a
  // stale limit or companion behind would save a chart the author never previewed.
  static CARD_FIELDS = ["metric_key", "dimension", "name", "subtitle", "description", "series", "limit",
                        "companion", "sort", "rollup", "card_type", "target_lines"];

  // A pencil on a live card in the edit grid: seed this tab from the card, show it, and open the dialog.
  startEditing(event) {
    const card = event.detail?.card;
    if (!card) return;

    this.editingCard = card;
    this.seedFrom(card);
    this.syncFooter();
    this.showThisTab();
    this.openDialog();
  }

  // Editing offers both outcomes; adding offers only the one, so the second button stays hidden there
  // rather than presenting the same action twice.
  syncFooter() {
    const editing = !!this.editingCard;

    if (this.hasPinNewButtonTarget) this.pinNewButtonTarget.classList.toggle("hidden", !editing);
    if (this.hasAddLabelTarget) this.addLabelTarget.textContent = editing ? "Update chart" : "Add";
    if (this.hasAddIconTarget) this.addIconTarget.classList.toggle("hidden", editing);
    this.syncTabs(editing);
  }

  // Editing is about ONE card, so the saved-chart tab beside it offers nothing -- and clicking it would
  // strand the edit on a panel with no way back. The strip is hidden and this panel's own heading takes
  // over the title the tab label was carrying.
  syncTabs(editing) {
    const list = this.dialog?.querySelector("[role='tablist']");
    // The tablist's own wrapper carries the bottom border, so hiding only the list would leave a rule
    // floating above the panel.
    (list?.parentElement ?? list)?.classList.toggle("hidden", editing);
    if (this.hasHeadingTarget) this.headingTarget.classList.toggle("sr-only", !editing);
    // The blurb sits directly under the heading, which takes no space while sr-only.
    if (this.hasBlurbTarget) this.blurbTarget.classList.toggle("mt-1.5", editing);
  }

  // The same selection, staged as a NEW card instead of rewritten onto the one being edited. Dropping
  // editingCard first is what makes add() take the staging path.
  pinAsNew() {
    this.editingCard = null;
    this.syncFooter();
    this.add();
  }

  seedFrom(card) {
    const read = (field) => this.cardField(card, field)?.value || "";

    // Through the select's OWN setter: the metric picker is a combobox that keeps its selection in a
    // private field, so writing the hidden input alone leaves the trigger showing the previous metric's
    // label -- the card would seed correctly and still read as the wrong metric.
    this.resetSelect(this.selectContainerFor(this.metricTarget), this.metricTarget, read("metric_key"));
    // metricChanged, not just the assignment: the breakdown group, the split/companion/limit options
    // and the generated card type are all derived from the metric.
    this.metricTarget.dispatchEvent(new Event("change", { bubbles: true }));
    this.selectDimension(read("dimension"));

    // EVERY remaining field, because add() writes all of CARD_FIELDS back: seeding only the metric and
    // breakdown left a card's split, limit, sort or goal lines to be cleared by an unrelated edit.
    // After the dimension, since the refine controls are only rendered once a breakdown is chosen.
    this.seedRefinements(read);

    // A name that is exactly what this pair generates is treated as still generated, so changing the
    // metric retitles the card. Anything else is the author's own and is left alone -- otherwise a
    // hand-typed title would be silently overwritten by the next metric change.
    //
    // The CARD says whether its name is generated: realtime_name_for takes the limit and companion, and
    // the client's names map is keyed dimension|series only, so a ranked title can be matched only
    // server-side. Field presence was the wrong proxy -- it stripped hand-typed titles off ranked cards.
    const name = read("name");
    if (name) {
      this.nameTarget.value = name;
      this.nameIsGenerated = read("name_generated") !== "";
    }
    this.schedulePreview();
  }

  seedRefinements(read) {
    const subtitle = this.element.querySelector("[id$='live_metric_subtitle']");
    if (subtitle) subtitle.value = read("subtitle");

    const description = this.element.querySelector("[id$='live_metric_description']");
    if (description) description.value = read("description");

    // Through each select's own setter, for the same reason the metric goes through resetSelect: these
    // are comboboxes holding their selection privately, so a bare input write leaves the trigger stale.
    [[this.hasSeriesTarget && this.seriesTarget, "series"],
     [this.hasCompanionTarget && this.companionTarget, "companion"],
     [this.hasLimitTarget && this.limitTarget, "limit"],
     [this.hasSortTarget && this.sortTarget, "sort"],
     [this.hasChartTypeTarget && this.chartTypeTarget, "card_type"]].forEach(([input, field]) => {
      if (input) this.resetSelect(this.selectContainerFor(input), input, read(field));
    });

    // The hidden field carries "1" when set and "" when not.
    if (this.hasRollupTarget) this.rollupTarget.checked = read("rollup") !== "";
    this.seedTargetLines(read("target_lines"));

    // The dependent syncs read every control above, so they run once the whole set is in place.
    this.syncDependents();
    // AFTER them: syncLimitField clears a limit on any bounded dimension, which would silently unrank a
    // saved ranked card the author never touched. A stored limit is the author's own choice, so it is
    // put back and its field shown.
    this.restoreStoredLimit(read);
    this.syncGeneratedCardType();
  }

  restoreStoredLimit(read) {
    const limit = read("limit");
    if (!this.hasLimitTarget || limit === "") return;

    this.limitFieldTarget?.classList.remove("hidden");
    this.resetSelect(this.selectContainerFor(this.limitTarget), this.limitTarget, limit);
    if (this.hasRollupTarget) this.rollupTarget.checked = read("rollup") !== "";
  }

  // resetSelect searches INSIDE what it is handed, and a select's hidden input sits within the select
  // ROOT -- so the root's parent is what has to be passed. Handed the root itself, the search finds
  // nothing and the reset silently degrades to writing the hidden value, leaving the trigger showing
  // the previous label: seeded correctly, reading as the wrong choice.
  selectContainerFor(input) {
    return input.closest('[data-controller~="select"]')?.parentElement || this.element;
  }

  // The dimension lives in whichever per-metric group the chosen metric renders, so it is set after
  // metricChanged has swapped the groups. Through the select's own setter, for the same reason the
  // metric is.
  selectDimension(value) {
    const input = this.element.querySelector(
      "[data-admin--report--live-metric-target='dimension']:not([disabled])",
    );
    if (!input) return;

    this.resetSelect(this.selectContainerFor(input), input, value);
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // Clicks the tab rather than toggling classes, so the tabs controller's own state stays authoritative.
  showThisTab() {
    const panel = this.panel;
    if (!panel) return;

    const panels = [...panel.parentElement.querySelectorAll("[data-ui--tabs-target='panel']")];
    const tabs = this.dialog?.querySelectorAll("[data-ui--tabs-target='tab']");
    tabs?.[panels.indexOf(panel)]?.click();
  }

  openDialog() {
    const dialog = this.dialog;
    if (!dialog) return;

    const scope = dialog.closest("[data-controller~='ui--dialog']");
    this.application
        .getControllerForElementAndIdentifier(scope, "ui--dialog")
        ?.openBy(dialog);
  }

  applyToCard(card, fields) {
    this.constructor.CARD_FIELDS.forEach((field) => {
      const value = field in fields ? fields[field] : "";
      const input = this.cardField(card, field);

      // A field the card never carried needs one creating: the author may have added a split or a limit
      // the original card had none of, and without an input it is silently dropped at Save.
      if (input) {
        input.value = value;
      } else if (value !== "") {
        card.insertAdjacentElement("afterbegin", this.buildCardField(field, value));
      }
    });

    // The card's visible title tracks the form, so an edited card doesn't keep reading as the old metric
    // until the page reloads. Blank means "let the server name it", so the form's own text is used.
    const heading = card.querySelector("input[name='cards[][name]']");
    if (heading) heading.value = fields.name || this.nameTarget.value.trim();

    this.gridChanged(card);
  }

  // The inverse of selectedTargetLines: the card carries "value:label" lines, the form carries PAIRED
  // target_lines[][value] / [][label] inputs. Seeding the wrong shape left them blank, and add() then
  // cleared the chart's saved goal lines.
  seedTargetLines(text) {
    const lines = text.split("\n").filter((line) => line.trim() !== "");
    // The panel is rendered once with config: {}, so it holds ONE goal-line row -- a card with more
    // would otherwise be rewritten with only its first line. Hidden pairs are appended for the rest,
    // matching the shape the form already posts for extra stored lines.
    this.element.querySelectorAll("[data-seeded-target-line]").forEach((node) => node.remove());
    const values = [...this.element.querySelectorAll("[name='target_lines[][value]']")];
    const labels = [...this.element.querySelectorAll("[name='target_lines[][label]']")];

    values.forEach((input, index) => {
      const [value, label] = this.splitTargetLine(lines[index]);
      input.value = value;
      if (labels[index]) labels[index].value = label;
    });

    // Built, never interpolated: a saved label is author text, and putting it in an HTML string would
    // parse markup out of it in the editor's browser.
    lines.slice(values.length).forEach((line) => {
      const [value, label] = this.splitTargetLine(line);
      const row = document.createElement("div");
      row.setAttribute("data-seeded-target-line", "");
      row.append(this.hiddenInput("target_lines[][value]", value),
                 this.hiddenInput("target_lines[][label]", label));
      values[0]?.closest("div")?.insertAdjacentElement("afterend", row);
    });
  }

  // The base returns "" for a bounded dimension, which is right when composing -- a bounded breakdown
  // needs no limit. But an edited card may already BE ranked on one, and returning "" there wrote the
  // ranking away on update. While the limit field is shown, whatever it holds is the author's choice.
  selectedLimit() {
    if (this.editingCard && this.hasLimitFieldTarget &&
        !this.limitFieldTarget.classList.contains("hidden") && this.hasLimitTarget) {
      return this.limitTarget.value;
    }

    return super.selectedLimit();
  }

  hiddenInput(name, value) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    return input;
  }

  // Split on the FIRST colon only: a label may contain one ("Goal: Q4").
  splitTargetLine(line) {
    if (!line) return ["", ""];

    const at = line.indexOf(":");
    return at === -1 ? [line, ""] : [line.slice(0, at), line.slice(at + 1)];
  }

  cardField(card, field) {
    // Textareas too: the grid stores the description in one, and matching only inputs made the dialog
    // seed blank and then append a duplicate field, splitting the card's row on submit.
    return card.querySelector(
      `input[name='cards[][${field}]'], textarea[name='cards[][${field}]']`,
    );
  }

  buildCardField(field, value) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = `cards[][${field}]`;
    input.value = value;
    input.setAttribute("form", "report-dashboard-form");
    return input;
  }

  // Arms the leave-confirmation guard, so navigating away from an edit that was never saved warns the
  // way an add or a resize does.
  gridChanged(card) {
    const grid = card.closest("[data-controller~='admin--report--grid']");
    if (grid) {
      this.application
          .getControllerForElementAndIdentifier(grid, "admin--report--grid")
          ?.dispatch("changed");
    }
  }

  // The X sits outside the tab panels, so it is found from the dialog rather than this panel.
  closeDialog() {
    (this.dialog ?? this.element).querySelector("[data-ui--dialog-target='closeButton']")?.click();
  }

  addCardController() {
    const scope = this.element.closest("[data-controller~='admin--report--add-card']");
    return this.application.getControllerForElementAndIdentifier(scope, "admin--report--add-card");
  }
}
